import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { apiClient } from '@/lib/api/client'
import type {
  ContactEventBreakdown,
  Organization,
  OrganizationContact,
  OrganizationContactEvents,
} from '@/lib/api/types'
import { useAuth } from '@/lib/auth'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_authenticated/contacts')({
  component: ContactsPage,
})

/**
 * Org-level attendee CRM. Access model: organizers see ONLY people who
 * attended THEIR organization's events, with metrics scoped to those events —
 * the backend endpoint (/organization/{id}/users/) enforces membership;
 * everything here is presentation.
 */
function ContactsPage() {
  const { user, token } = useAuth()

  // Organizers: their own organizations (usually exactly one).
  const { data: myOrgs, isLoading: loadingMine } = useQuery({
    queryKey: ['my-organizations'],
    queryFn: () => apiClient.getUserOrganizations(token ?? ''),
    enabled: !!token && !user?.is_superuser,
  })
  // Superusers: any organization.
  const { data: allOrgs, isLoading: loadingAll } = useQuery({
    queryKey: ['ops-organizations'],
    queryFn: () => apiClient.getOrganizationsList(token ?? ''),
    enabled: !!token && !!user?.is_superuser,
  })

  const organizations: Organization[] = user?.is_superuser
    ? (allOrgs ?? [])
    : (myOrgs?.organizations ?? [])
  const loading = user?.is_superuser ? loadingAll : loadingMine

  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null)
  const orgId = selectedOrgId ?? organizations[0]?.id ?? null

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white">Contacts</h1>
          <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
            Everyone who has attended your events — with attendance and spend at your events only.
          </p>
        </div>
        {organizations.length > 1 && (
          <label className="text-xs text-neutral-500 dark:text-neutral-400">
            Organization
            <select
              className="block mt-1 px-2 py-1.5 text-sm border border-neutral-200 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100"
              value={orgId ?? ''}
              onChange={(e) => setSelectedOrgId(Number(e.target.value) || null)}
            >
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-neutral-500 dark:text-neutral-400">Loading…</p>
      ) : !orgId ? (
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          You don&apos;t administer any organization.
        </p>
      ) : (
        <ContactsTable orgId={orgId} />
      )}
    </div>
  )
}

const SORTS: Array<{ key: string; label: string; align: 'left' | 'right' }> = [
  { key: 'first_name', label: 'Name', align: 'left' },
  { key: '', label: 'Email', align: 'left' },
  { key: '', label: 'Phone', align: 'left' },
  { key: 'events_attended', label: 'Events', align: 'right' },
  { key: 'total_orders', label: 'Orders', align: 'right' },
  { key: 'total_spent', label: 'Total Spent', align: 'right' },
  { key: 'last_attended', label: 'Last Attended', align: 'right' },
  { key: 'date_joined', label: 'Joined', align: 'right' },
]

function ContactsTable({ orgId }: { orgId: number }) {
  const { token } = useAuth()
  const [selected, setSelected] = useState<OrganizationContact | null>(null)
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [page, setPage] = useState(1)
  const [ordering, setOrdering] = useState('-date_joined')

  useEffect(() => {
    const id = setTimeout(() => { setDebounced(search); setPage(1) }, 300)
    return () => clearTimeout(id)
  }, [search])

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['org-contacts', orgId, page, debounced, ordering],
    queryFn: () => apiClient.getOrganizationUsers(token ?? '', orgId, {
      page,
      search: debounced,
      ordering,
    }),
    enabled: !!token,
    placeholderData: (prev) => prev,
  })

  const toggleSort = (key: string) => {
    if (!key) return
    setPage(1)
    setOrdering((cur) => (cur === `-${key}` ? key : `-${key}`))
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.count / 100)) : 1

  return (
    <div className="bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800">
      <div className="px-5 py-4 border-b border-neutral-200 dark:border-neutral-800 flex flex-wrap items-center justify-between gap-3">
        <input
          className="w-full sm:w-80 px-3 py-2 text-sm border border-neutral-200 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100"
          placeholder="Search name, email or phone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="text-xs text-neutral-500 dark:text-neutral-400">
          {data ? `${data.count.toLocaleString()} contact${data.count === 1 ? '' : 's'}` : ''}
          {isFetching ? ' · refreshing…' : ''}
        </span>
      </div>

      {error && (
        <p className="px-5 py-4 text-sm text-amber-700 dark:text-amber-400">
          {(error as Error).message}
        </p>
      )}

      {isLoading ? (
        <p className="px-5 py-8 text-sm text-neutral-500 dark:text-neutral-400">Loading…</p>
      ) : !data || data.results.length === 0 ? (
        <p className="px-5 py-8 text-sm text-neutral-500 dark:text-neutral-400">No contacts found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400 border-b border-neutral-200 dark:border-neutral-800">
                {SORTS.map((col) => (
                  <th
                    key={col.label}
                    className={cn('font-medium px-4 py-3 first:pl-5 last:pr-5',
                      col.align === 'right' ? 'text-right' : 'text-left',
                      col.key && 'cursor-pointer select-none hover:text-neutral-800 dark:hover:text-neutral-200')}
                    onClick={() => toggleSort(col.key)}
                  >
                    {col.label}
                    {col.key && ordering.replace('-', '') === col.key && (
                      <span className="ml-1">{ordering.startsWith('-') ? '↓' : '↑'}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.results.map((contact) => (
                <ContactRow key={contact.id} contact={contact} onSelect={setSelected} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <ContactDrawer orgId={orgId} contact={selected} onClose={() => setSelected(null)} />
      )}

      {data && data.count > 100 && (
        <div className="px-5 py-3 border-t border-neutral-200 dark:border-neutral-800 flex items-center justify-between text-sm">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="px-3 py-1.5 rounded-md border border-neutral-200 dark:border-neutral-700 disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            Page {page} of {totalPages}
          </span>
          <button
            disabled={!data.next}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 rounded-md border border-neutral-200 dark:border-neutral-700 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  )
}

function ContactRow({ contact, onSelect }: {
  contact: OrganizationContact
  onSelect: (c: OrganizationContact) => void
}) {
  const name = `${contact.first_name ?? ''} ${contact.last_name ?? ''}`.trim()
  return (
    <tr
      onClick={() => onSelect(contact)}
      className="border-b border-neutral-100 dark:border-neutral-800 last:border-0 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 cursor-pointer"
    >
      <td className="px-4 py-3 pl-5 font-medium text-neutral-900 dark:text-white">
        {name || '—'}
      </td>
      {/* email/phone are server-masked to null for callers without contact
          visibility permission — render the mask, don't invent data */}
      <td className="px-4 py-3 text-neutral-600 dark:text-neutral-400">{contact.email ?? '—'}</td>
      <td className="px-4 py-3 text-neutral-600 dark:text-neutral-400 tabular-nums">{contact.phone_number ?? '—'}</td>
      <td className="px-4 py-3 text-right tabular-nums">{contact.events_attended ?? 0}</td>
      <td className="px-4 py-3 text-right tabular-nums">{contact.total_orders ?? 0}</td>
      <td className="px-4 py-3 text-right tabular-nums font-medium">
        {contact.total_spent != null
          ? `$${contact.total_spent.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          : '—'}
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-neutral-600 dark:text-neutral-400">
        {contact.last_attended ? contact.last_attended.slice(0, 10) : '—'}
      </td>
      <td className="px-4 py-3 pr-5 text-right tabular-nums text-neutral-600 dark:text-neutral-400">
        {contact.date_joined ? contact.date_joined.slice(0, 10) : '—'}
      </td>
    </tr>
  )
}


function money(value: number | undefined): string {
  return `$${(value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function tokens(value: number | undefined): string {
  return (value ?? 0).toLocaleString()
}

/**
 * Right slide-over showing one contact's per-event history within the
 * selected organization — the /users/{id}/events/ endpoint is org-scoped
 * server-side, so nothing outside this organization ever appears here.
 * Unit caution (matches the API): add-credit and card refunds are DOLLARS;
 * spend / voucher / transfer / balance figures are TOKENS.
 */
function ContactDrawer({ orgId, contact, onClose }: {
  orgId: number
  contact: OrganizationContact
  onClose: () => void
}) {
  const { token } = useAuth()

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const { data, isLoading, error } = useQuery<OrganizationContactEvents>({
    queryKey: ['org-contact-events', orgId, contact.id],
    queryFn: () => apiClient.getOrganizationUserEvents(token ?? '', orgId, contact.id),
    enabled: !!token,
  })

  const totals = data?.totals
  const name = data?.user_name?.trim()
    || `${contact.first_name ?? ''} ${contact.last_name ?? ''}`.trim()
    || '—'

  return (
    // Above the app chrome: the sidebar is fixed at z-50, so the overlay
    // must beat it or the nav floats over the backdrop.
    <div className="fixed inset-0 z-[60]">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full sm:w-[520px] bg-white dark:bg-neutral-900 shadow-xl border-l border-neutral-200 dark:border-neutral-800 flex flex-col">
        <div className="px-5 py-4 border-b border-neutral-200 dark:border-neutral-800 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white truncate">{name}</h2>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
              {[data?.user_email ?? contact.email, data?.user_phone ?? contact.phone_number]
                .filter(Boolean).join(' · ') || '—'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 px-2 py-1 text-sm rounded-md text-neutral-500 hover:text-neutral-900 dark:hover:text-white"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <p className="px-5 py-8 text-sm text-neutral-500 dark:text-neutral-400">Loading…</p>
          ) : error ? (
            <p className="px-5 py-8 text-sm text-amber-700 dark:text-amber-400">
              {(error as Error).message}
            </p>
          ) : data ? (
            <>
              {/* Preview: the four headline points — depth lives on the profile */}
              <div className="px-5 py-4 border-b border-neutral-200 dark:border-neutral-800">
                <div className="grid grid-cols-4 gap-3">
                  <DrawerStat
                    label="Loaded"
                    value={money(totals?.total_base
                      ?? Math.max(0, (totals?.total_add_credit ?? 0) - (totals?.total_tax ?? 0) - (totals?.total_fee ?? 0)))}
                  />
                  <DrawerStat label="Orders" value={tokens(totals?.total_orders)} />
                  <DrawerStat label="Items" value={tokens(totals?.items_ordered)} />
                  <DrawerStat
                    label="Balance"
                    value={totals?.total_balance_value != null
                      ? money(totals.total_balance_value)
                      // deployed API predates balance_value — tokens until then
                      : tokens(totals?.total_balance)}
                  />
                </div>
                <Link
                  to="/contacts/$userId"
                  params={{ userId: String(contact.id) }}
                  search={{ org: orgId }}
                  className="mt-4 block w-full text-center px-3 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700"
                >
                  View profile
                </Link>
              </div>

              <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
                {data.events.map((ev) => <DrawerEvent key={ev.event_id} ev={ev} />)}
                {data.events.length === 0 && (
                  <li className="px-5 py-8 text-sm text-neutral-500 dark:text-neutral-400">
                    No event history in this organization.
                  </li>
                )}
              </ul>
            </>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function DrawerStat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-neutral-400 dark:text-neutral-500">{label}</p>
      <p className="text-sm font-semibold tabular-nums text-neutral-900 dark:text-white">{value}</p>
    </div>
  )
}

function DrawerEvent({ ev }: { ev: ContactEventBreakdown }) {
  return (
    <li className="px-5 py-4">
      <div className="flex items-center gap-3">
        {ev.event_logo ? (
          <img src={ev.event_logo} alt="" className="w-8 rounded object-cover shrink-0" />
        ) : (
          <span className="h-8 w-8 rounded bg-neutral-200 dark:bg-neutral-700 flex items-center justify-center text-xs font-semibold text-neutral-500 dark:text-neutral-300 shrink-0">
            {ev.event_name.charAt(0)}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">{ev.event_name}</p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Attended {ev.attended_date ? ev.attended_date.slice(0, 10) : '—'}
            {ev.last_transaction ? ` · last activity ${ev.last_transaction.slice(0, 10)}` : ''}
          </p>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-4 gap-x-4 gap-y-2">
        <DrawerStat
          label="Loaded"
          value={money(ev.total_base
            ?? Math.max(0, (ev.total_add_credit ?? 0) - (ev.total_tax ?? 0) - (ev.total_fee ?? 0)))}
        />
        <DrawerStat label="Orders" value={tokens(ev.total_orders)} />
        <DrawerStat label="Items" value={tokens(ev.items_ordered)} />
        <DrawerStat
          label="Balance"
          value={ev.balance_value != null ? money(ev.balance_value) : tokens(ev.balance)}
        />
      </div>
    </li>
  )
}
